/**
 * Room availability resolution — pure logic, no Firestore.
 *
 * Layers (highest precedence first):
 *   1. room.alwaysAvailable === true            -> open
 *   2. week exception doc entry for the date    -> its slots (empty/closed = closed day)
 *   3. legacy week doc (room has no template):
 *      doc exists with entries for other days   -> closed day (old editor omitted closed days)
 *   4. red-severity holiday + room.blockOnHolidays === true -> holiday block
 *   5. room.scheduleTemplate weekday entry      -> its slots (empty = closed day)
 *   6. nothing configured                       -> open (default-open, backward compatible)
 *
 * Slot times are naive "HH:MM" wall-clock strings in Asia/Jerusalem.
 */

export const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export const timeToMinutes = (time) => {
  if (typeof time !== "string") return null;
  const [h, m] = time.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

export const minutesToTime = (mins) => {
  const h = String(Math.floor(mins / 60)).padStart(2, "0");
  const m = String(mins % 60).padStart(2, "0");
  return `${h}:${m}`;
};

/** Weekday key ("sunday".."saturday") for a "YYYY-MM-DD" string. */
export const weekdayKeyForDateStr = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  // Noon avoids any DST edge shifting the calendar day
  const d = new Date(`${dateStr}T12:00:00`);
  if (isNaN(d.getTime())) return null;
  return WEEKDAY_KEYS[d.getDay()];
};

/**
 * Sort + merge overlapping/adjacent slots so an appointment spanning two
 * touching slots (09-12 + 12-15) is not falsely rejected.
 * Input/output: [{start:"HH:MM", end:"HH:MM"}]
 */
export const mergeSlots = (slots) => {
  const valid = (slots || [])
    .map((s) => ({
      start: timeToMinutes(s.start),
      end: timeToMinutes(s.end),
    }))
    .filter((s) => s.start !== null && s.end !== null && s.end > s.start)
    .sort((a, b) => a.start - b.start);

  const merged = [];
  for (const slot of valid) {
    const last = merged[merged.length - 1];
    if (last && slot.start <= last.end) {
      last.end = Math.max(last.end, slot.end);
    } else {
      merged.push({ ...slot });
    }
  }
  return merged.map((s) => ({
    start: minutesToTime(s.start),
    end: minutesToTime(s.end),
  }));
};

const isWithinSlots = (mergedSlots, startMins, endMins) =>
  mergedSlots.some(
    (s) => startMins >= timeToMinutes(s.start) && endMins <= timeToMinutes(s.end)
  );

const formatHours = (mergedSlots) =>
  mergedSlots.map((s) => `${s.start}-${s.end}`).join(", ");

/**
 * Resolve which slots (if any) apply to a given date, before time comparison.
 *
 * @param {object} params
 * @param {object} params.room     room doc data (alwaysAvailable, blockOnHolidays, scheduleTemplate)
 * @param {object|null} params.weekDoc  availability/{weekId} doc data ({slots:[{date, slots, closed?}]}) or null
 * @param {string} params.dateStr  "YYYY-MM-DD" (Israel-local)
 * @param {object|null} params.holiday  result of getHolidayForDateStr(dateStr) or null
 * @returns {{ mode: 'open'|'slots'|'closed'|'holiday', slots: Array, source: string, holidayName?: string }}
 */
export const resolveRoomDay = ({ room, weekDoc, dateStr, holiday }) => {
  if (room?.alwaysAvailable === true) {
    return { mode: "open", slots: [], source: "always" };
  }

  const hasTemplate =
    room?.scheduleTemplate && typeof room.scheduleTemplate === "object";

  // Layer 2: explicit per-date exception (also how legacy week docs stored hours)
  const weekEntries = weekDoc?.slots || [];
  const dayEntry = weekEntries.find((d) => d.date === dateStr);
  if (dayEntry) {
    if (dayEntry.closed === true || !(dayEntry.slots || []).length) {
      return { mode: "closed", slots: [], source: "exception" };
    }
    return { mode: "slots", slots: mergeSlots(dayEntry.slots), source: "exception" };
  }

  // Layer 3: legacy semantics — old editor saved only open days, so a week doc
  // with entries but none for this date meant "closed". Applies only while the
  // room has no recurring template (new editor writes explicit closed entries).
  if (!hasTemplate && weekEntries.length > 0) {
    return { mode: "closed", slots: [], source: "exception" };
  }

  // Layer 4: holiday auto-block (red severity only), unless exception opened it above
  if (holiday?.severity === "red" && room?.blockOnHolidays === true) {
    return { mode: "holiday", slots: [], source: "holiday", holidayName: holiday.name };
  }

  // Layer 5: recurring weekly template
  if (hasTemplate) {
    const weekdayKey = weekdayKeyForDateStr(dateStr);
    const daySlots = room.scheduleTemplate?.[weekdayKey]?.slots || [];
    if (!daySlots.length) {
      return { mode: "closed", slots: [], source: "template" };
    }
    return { mode: "slots", slots: mergeSlots(daySlots), source: "template" };
  }

  // Layer 6: nothing configured — room is open (backward compatible default)
  return { mode: "open", slots: [], source: "default_open" };
};

/**
 * Full pure resolution for a candidate time range.
 *
 * @returns {{ available: boolean,
 *             reason: null|'closed_day'|'outside_hours'|'holiday',
 *             message: string|null,
 *             slotsForDate: Array<{start,end}>,
 *             source: string, holidayName?: string }}
 */
export const resolveRoomSlotPure = ({
  room,
  weekDoc,
  dateStr,
  holiday,
  startMins,
  endMins,
}) => {
  const day = resolveRoomDay({ room, weekDoc, dateStr, holiday });

  if (day.mode === "open") {
    return {
      available: true,
      reason: null,
      message: null,
      slotsForDate: [],
      source: day.source,
    };
  }

  if (day.mode === "holiday") {
    return {
      available: false,
      reason: "holiday",
      message: `החדר סגור בחג: ${day.holidayName}`,
      slotsForDate: [],
      source: day.source,
      holidayName: day.holidayName,
    };
  }

  if (day.mode === "closed") {
    return {
      available: false,
      reason: "closed_day",
      message: "לא נפתחה זמינות לחדר ביום זה",
      slotsForDate: [],
      source: day.source,
    };
  }

  // mode === 'slots'
  if (isWithinSlots(day.slots, startMins, endMins)) {
    return {
      available: true,
      reason: null,
      message: null,
      slotsForDate: day.slots,
      source: day.source,
    };
  }

  return {
    available: false,
    reason: "outside_hours",
    message: `החדר זמין ביום זה בשעות: ${formatHours(day.slots)}`,
    slotsForDate: day.slots,
    source: day.source,
  };
};

/**
 * Free-gap math for "nearest free slots" suggestions.
 * openWindows: merged [{start,end}] (pass [{start:"07:00",end:"22:00"}] for default-open days)
 * busy: [{startMins, endMins}] existing bookings that day
 * Returns up to `limit` gaps of >= durationMins, ordered by distance from preferredStartMins.
 */
export const findFreeGaps = ({
  openWindows,
  busy,
  durationMins,
  preferredStartMins,
  limit = 3,
}) => {
  const busySorted = (busy || [])
    .filter((b) => b.endMins > b.startMins)
    .sort((a, b) => a.startMins - b.startMins);

  const gaps = [];
  for (const win of openWindows || []) {
    let cursor = timeToMinutes(win.start);
    const winEnd = timeToMinutes(win.end);
    for (const b of busySorted) {
      if (b.endMins <= cursor || b.startMins >= winEnd) continue;
      if (b.startMins - cursor >= durationMins) {
        gaps.push({ startMins: cursor, endMins: b.startMins });
      }
      cursor = Math.max(cursor, b.endMins);
    }
    if (winEnd - cursor >= durationMins) {
      gaps.push({ startMins: cursor, endMins: winEnd });
    }
  }

  // Candidate start inside each gap closest to the preferred start
  const candidates = gaps.map((g) => {
    const start = Math.min(
      Math.max(g.startMins, preferredStartMins),
      g.endMins - durationMins
    );
    return {
      start: minutesToTime(start),
      end: minutesToTime(start + durationMins),
      distance: Math.abs(start - preferredStartMins),
    };
  });

  return candidates
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit)
    .map(({ start, end }) => ({ start, end }));
};
